import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-ink text-paper relative z-10">
      <div className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full border border-paper/20 flex items-center justify-center">
                <span className="font-jp text-sm text-paper/80 tracking-wider">暖</span>
              </div>
              <span className="font-serif text-2xl font-semibold tracking-wide text-paper">NOREN</span>
            </div>
            <p className="text-paper/50 text-sm leading-relaxed mb-6 max-w-xs">
              {t("footer.description")}
            </p>
            <div className="flex items-center gap-3">
              {["instagram", "linkedin", "youtube"].map((social) => (
                <a
                  key={social}
                  href={`https://${social}.com`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 rounded-full border border-paper/15 flex items-center justify-center text-paper/40 hover:border-gold hover:text-gold transition-colors duration-300"
                  aria-label={social}
                >
                  <i className={`ri-${social}-line text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-paper/30 mb-5">{t("footer.navigate")}</h4>
            <ul className="space-y-3">
              {[
                { label: t("nav.home"), href: "#listings" },
                { label: t("nav.services"), href: "#services" },
                { label: t("nav.howItWorks"), href: "#how-it-works" },
                { label: t("nav.returns"), href: "#returns" },
                { label: t("nav.honestFAQ"), href: "#honest-faq" },
                { label: t("nav.contact"), href: "#inquiry" },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-sm text-paper/50 hover:text-paper transition-colors duration-300 cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-paper/30 mb-5">{t("footer.contact")}</h4>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-paper/25 mb-1">{t("footer.office")}</p>
                <p className="text-sm text-paper/50">{t("footer.nagano")}</p>
                <p className="text-sm text-paper/50">{t("footer.japan")}</p>
              </div>
              <div>
                <p className="text-xs text-paper/25 mb-1">{t("footer.email")}</p>
                <a href="mailto:hello@noren.jp" className="text-sm text-paper/50 hover:text-paper transition-colors">hello@noren.jp</a>
              </div>
              <div>
                <p className="text-xs text-paper/25 mb-1">{t("footer.hours")}</p>
                <p className="text-sm text-paper/50">JST 09:00 – 18:00</p>
                <p className="text-sm text-paper/50">{t("footer.weekdays")}</p>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <img
              src="https://readdy.ai/api/search-image?query=A%20serene%20Japanese%20interior%20with%20minimalist%20wooden%20architecture%20soft%20natural%20light%20through%20shoji%20screens%20warm%20earth%20tones%20and%20clean%20editorial%20photography%20style%20for%20a%20luxury%20brand%20website%20footer%20decoration&width=600&height=400&seq=noren-footer&orientation=landscape"
              alt="Serene Japanese interior with minimalist wooden architecture and soft natural light through shoji screens"
              loading="lazy"
              className="w-full h-48 object-cover border border-paper/10 opacity-60"
            />
          </div>
        </div>

        <div className="border-t border-paper/10 mt-12 pt-8">
          <p className="text-xs text-paper/30 leading-relaxed mb-4 max-w-3xl">
            <strong className="text-paper/50">{t("footer.disclaimerTitle")}</strong> {t("footer.disclaimerText")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-paper/30">{t("footer.copyright", { year: new Date().getFullYear() })}</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-xs text-paper/30 hover:text-paper transition-colors">{t("footer.privacy")}</Link>
              <Link to="/terms" className="text-xs text-paper/30 hover:text-paper transition-colors">{t("footer.terms")}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}